/** @jsx jsx */
import { jsx } from "theme-ui";
import React from "react";
import { StickyProvider } from "contexts/app/app.provider";
import SEO from "components/seo";
import Layout from "components/layout";
import { Box, Container, Flex, Heading, Text, Button } from "theme-ui";
import { useRequireWorkshopSession } from "hooks/use-require-workshop-session";
import { useAuthStore } from "stores/auth-store";
import { FaCheck, FaCheckCircle, FaShieldAlt, FaExternalLinkAlt } from "react-icons/fa";
import { getInsuranceUrl } from "utils/insurance";

export default function InsurancePage() {
  const { ready, bootstrapping } = useRequireWorkshopSession("/insurance/");
  const user = useAuthStore((state) => state.user);

  if (bootstrapping || !ready) {
    return (
      <StickyProvider>
        <Layout>
          <SEO
            title="Garaaz - Buy Insurance"
            description="Buy motor insurance for your workshop vehicles"
            keywords="garaaz, motor insurance, car insurance"
          />
          <Box
            sx={{
              minHeight: "calc(100vh - 120px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bg: "background_secondary",
              py: [6, 7, 8],
              pt: ["100px", "110px", "120px"],
            }}
          >
            <Flex
              sx={{
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
              }}
            >
              {/* Elegant CSS spinner */}
              <Box
                sx={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  border: "3px solid #E2E8F0",
                  borderTopColor: "primary",
                  animation: "spin 1s linear infinite",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
              <Text sx={{ color: "muted", fontSize: 2 }}>
                {bootstrapping ? "Verifying sign-in…" : "Redirecting to login…"}
              </Text>
            </Flex>
          </Box>
        </Layout>
      </StickyProvider>
    );
  }

  const insuranceUrl = getInsuranceUrl(user);

  const handleProceedClick = () => {
    window.open(insuranceUrl, "_blank");
  };

  return (
    <StickyProvider>
      <Layout>
        <SEO
          title="Buy Insurance | Garaaz"
          description="Access our secure motor insurance portal partnered with Square Insurance"
          keywords="garaaz, motor insurance, car insurance, square insurance"
        />

        <Box
          as="section"
          sx={{
            minHeight: "calc(100vh - 120px)",
            display: "flex",
            alignItems: "center",
            bg: "#F5F7FA",
            py: [6, 7, 8],
            pt: ["110px", "120px", "130px"],
          }}
        >
          <Container>
            <Box
              sx={{
                maxWidth: "540px",
                mx: "auto",
                bg: "background",
                borderRadius: "24px",
                boxShadow: "0 10px 40px rgba(15, 33, 55, 0.08)",
                p: [4, 5],
                border: "1px solid",
                borderColor: "border_color",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 14px 48px rgba(15, 33, 55, 0.12)",
                },
              }}
            >
              {/* Header Status Badge */}
              <Flex sx={{ justifyContent: "center", mb: 3 }}>
                <Flex
                  sx={{
                    alignItems: "center",
                    gap: 2,
                    bg: "#E6FFFA",
                    color: "#0D9488",
                    px: 3,
                    py: 1,
                    borderRadius: "30px",
                    fontSize: 1,
                    fontWeight: 600,
                    border: "1px solid #B2F5EA",
                  }}
                >
                  <FaCheckCircle style={{ fontSize: "14px" }} />
                  <span>You are now logged in</span>
                </Flex>
              </Flex>


              {/* Click to Proceed Action Button */}
              <Box sx={{ mb: 4 }}>
                <Button
                  onClick={handleProceedClick}
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    fontSize: 2,
                    fontWeight: 700,
                    py: 3,
                    borderRadius: "14px",
                    bg: "primary",
                    color: "white",
                    cursor: "pointer",
                    boxShadow: "0 8px 20px rgba(31, 62, 118, 0.25)",
                    transition: "all 0.25s ease",
                    "&:hover": {
                      bg: "secondary",
                      boxShadow: "0 10px 24px rgba(37, 203, 158, 0.35)",
                      transform: "translateY(-1px)",
                    },
                    "&:active": {
                      transform: "translateY(1px)",
                    },
                  }}
                >
                  <span>Click to proceed to the Insurance Portal</span>
                  <FaExternalLinkAlt style={{ fontSize: "13px" }} />
                </Button>
              </Box>

              {/* Features List */}
              <Box sx={{ mb: 4 }}>
                <Heading
                  as="h3"
                  sx={{
                    fontSize: 1,
                    color: "heading_secondary",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    mb: 3,
                  }}
                >
                  Benefits at a Glance
                </Heading>

                <Flex sx={{ flexDirection: "column", gap: 3 }}>
                  <Flex sx={{ gap: 3, alignItems: "flex-start" }}>
                    <Flex
                      sx={{
                        width: "24px",
                        height: "24px",
                        borderRadius: "50%",
                        bg: "#EBF8FF",
                        color: "primary",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        mt: "2px",
                      }}
                    >
                      <FaCheck style={{ fontSize: "10px" }} />
                    </Flex>
                    <Box>
                      <Text
                        sx={{
                          fontWeight: 700,
                          fontSize: 1,
                          color: "text_secondary",
                          display: "block",
                        }}
                      >
                        Quick Quotes
                      </Text>
                      <Text sx={{ fontSize: 1, color: "muted", lineHeight: 1.4 }}>
                        Get competitive quotes from multiple top insurance providers in seconds.
                      </Text>
                    </Box>
                  </Flex>

                  <Flex sx={{ gap: 3, alignItems: "flex-start" }}>
                    <Flex
                      sx={{
                        width: "24px",
                        height: "24px",
                        borderRadius: "50%",
                        bg: "#EBF8FF",
                        color: "primary",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        mt: "2px",
                      }}
                    >
                      <FaCheck style={{ fontSize: "10px" }} />
                    </Flex>
                    <Box>
                      <Text
                        sx={{
                          fontWeight: 700,
                          fontSize: 1,
                          color: "text_secondary",
                          display: "block",
                        }}
                      >
                        Multiple Providers
                      </Text>
                      <Text sx={{ fontSize: 1, color: "muted", lineHeight: 1.4 }}>
                        Compare plans side-by-side to choose the best coverage.
                      </Text>
                    </Box>
                  </Flex>

                  <Flex sx={{ gap: 3, alignItems: "flex-start" }}>
                    <Flex
                      sx={{
                        width: "24px",
                        height: "24px",
                        borderRadius: "50%",
                        bg: "#EBF8FF",
                        color: "primary",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        mt: "2px",
                      }}
                    >
                      <FaCheck style={{ fontSize: "10px" }} />
                    </Flex>
                    <Box>
                      <Text
                        sx={{
                          fontWeight: 700,
                          fontSize: 1,
                          color: "text_secondary",
                          display: "block",
                        }}
                      >
                        Instant Approval
                      </Text>
                      <Text sx={{ fontSize: 1, color: "muted", lineHeight: 1.4 }}>
                        Seamless digital policy issuance with minimal documentation.
                      </Text>
                    </Box>
                  </Flex>
                </Flex>
              </Box>



              {/* Security Redirection Notice */}
              <Flex
                sx={{
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  color: "muted",
                }}
              >
                <FaShieldAlt
                  style={{ fontSize: "14px", color: "secondary", flexShrink: 0 }}
                />
                <Text sx={{ fontSize: 0, textAlign: "center", lineHeight: 1.4 }}>
                  You are being redirected to our secure partner portal.
                </Text>
              </Flex>
            </Box>
          </Container>
        </Box>
      </Layout>
    </StickyProvider>
  );
}
